'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useMutation } from '@tanstack/react-query';
import { Globe, Check } from 'lucide-react';
import { useTRPC } from '@/lib/trpc/provider';
import { useAuthStore } from '@/hooks/use-auth';
import { writeLocaleCookie } from '@/lib/i18n/locale-cookie';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/validators/user.schema';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  'zh-CN': '中文 (简体)',
  ko: '한국어',
  ja: '日本語',
};

const LOCALE_SHORT: Record<SupportedLocale, string> = {
  en: 'EN',
  'zh-CN': '中文',
  ko: '한국',
  ja: '日本',
};

// Locales whose translations are AI-drafted and not yet human-reviewed. Marked
// with a small "beta" tag in the switcher so users know quality is provisional.
const LOCALE_UNREVIEWED = new Set<SupportedLocale>(['ko', 'ja']);

interface LanguageSwitcherProps {
  // 'dropdown' (default) = small popout, used on desktop. 'drawer' = full-width
  // bottom sheet for mobile, where a tiny popout is awkward to tap.
  variant?: 'dropdown' | 'drawer';
}

export function LanguageSwitcher({ variant = 'dropdown' }: LanguageSwitcherProps) {
  const trpc = useTRPC();
  const router = useRouter();
  const t = useTranslations('public.header');
  const user = useAuthStore((s) => s.user);
  const setLanguage = useAuthStore((s) => s.setLanguage);
  // The active locale is the single source of truth, seeded server-side by the
  // surrounding provider (PublicI18nProvider reads the NEXT_LOCALE cookie on the
  // server; AdminI18nProvider uses the account preference). Reading it here —
  // rather than the client-only cookie — keeps SSR and the first client render
  // in agreement, so there's no hydration mismatch on the label.
  const currentLocale = useLocale() as SupportedLocale;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const mutation = useMutation(
    trpc.user.updateLanguage.mutationOptions({})
  );

  function handleSelect(locale: SupportedLocale) {
    if (locale === currentLocale) return;
    const previousLocale = currentLocale;

    // Always persist the choice in the cookie — the single source of truth for
    // anonymous visitors and what the (public) server layout reads for SSR.
    writeLocaleCookie(locale);
    setLanguage(locale);

    if (user) {
      // Authenticated: also sync the account preference. Cookie + account stay
      // in lockstep; the optimistic store update rolls back on error.
      mutation.mutate(
        { language: locale },
        {
          onError: () => {
            writeLocaleCookie(previousLocale);
            setLanguage(previousLocale);
          },
        }
      );
    } else {
      // Anonymous: re-run the (public) server layout so it re-reads the cookie
      // and re-renders with the new message bundle (no account mutation).
      router.refresh();
    }
  }

  const trigger = (
    <Button
      variant="ghost"
      size="sm"
      className="text-[var(--eifo-primary)] hover:text-[var(--eifo-primary)]/80 gap-1"
      aria-label={t('changeLanguage')}
    >
      <Globe className="h-4 w-4" />
      <span className="text-xs font-medium">{LOCALE_SHORT[currentLocale]}</span>
    </Button>
  );

  // ── Drawer variant (mobile) ──
  if (variant === 'drawer') {
    return (
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="pb-[max(1rem,env(safe-area-inset-bottom))]">
          <SheetHeader>
            <SheetTitle>{t('language')}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col p-2">
            {SUPPORTED_LOCALES.map((locale) => (
              <button
                key={locale}
                onClick={() => { handleSelect(locale); setDrawerOpen(false); }}
                aria-current={locale === currentLocale ? 'true' : undefined}
                className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-base hover:bg-[#f1f5f9]"
              >
                <span className={locale === currentLocale ? 'font-semibold' : ''}>
                  {LOCALE_LABELS[locale]}
                </span>
                {LOCALE_UNREVIEWED.has(locale) && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700">
                    beta
                  </span>
                )}
                {locale === currentLocale && <Check className="ml-auto h-4 w-4 text-[#1e306e]" />}
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ── Dropdown variant (desktop, default) ──
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleSelect(locale)}
            className={`gap-2 ${locale === currentLocale ? 'font-semibold' : ''}`}
          >
            {LOCALE_LABELS[locale]}
            {LOCALE_UNREVIEWED.has(locale) && (
              <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700">
                beta
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
